import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';

import { CoreDataService, LoadService } from '~/api-services';
import { SESSION_EXPIRED, UNAUTHORIZED_STATUS_CODES } from '~/api-services/baseClient';
import type { UserData, Account, InitialData, Trades } from '~/api-services/LoadService/types';
import type { KeepALive } from '~/api-services/CoreDataService/types';
import { thunkGenericApiReducerBuilder } from './utils';
import type { ThunkAPI } from '../store';
import { loadAvatars } from "~/state/reducers/avatarSlice";

type UserDataType = Pick<UserData, 'traderId' | 'account' | 'accountDetails' | 'pendingMessagesCount' | 'openTradesActive' | 'openTradesConsumers' | 'notificationsActive' >;

export interface UserDataState extends ResponseMetadata {
    data: UserDataType | null;
}

const initialState: UserDataState = {
    isFetching: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
    data: null,
};

export const loadUserData = createAsyncThunk<InitialData, void, ThunkAPI>('userData/load', async (_, thunkAPI) => {
    try {
        const { status, data } = await LoadService.getInitialData();
        if (status !== 200 || !data.returnData) {
            throw new Error(data.errorCode || 'Unauthorized');
        }
        thunkAPI.dispatch(loadAvatars());
        return data.returnData;
    } catch (e) {
        return thunkAPI.rejectWithValue('Unauthorized');
    }
});

const isSessionError = (e: any, errorCode?: string) =>
    errorCode === SESSION_EXPIRED ||
    e?.response?.data?.errorCode === SESSION_EXPIRED ||
    UNAUTHORIZED_STATUS_CODES.includes(e?.response?.status);

export const loadUserKeepALiveData = createAsyncThunk<KeepALive, void, ThunkAPI>(
    'userData/loadKeepALive',
    async (_, thunkAPI) => {
        try {
            const otherState = thunkAPI.getState().userData.data;
            const { status, data } = await CoreDataService.getKeepALive(otherState.openTradesActive, otherState.notificationsActive);
            if (status !== 200 || !data.returnData) {
                if (isSessionError(null, data.errorCode)) {
                    return thunkAPI.rejectWithValue(SESSION_EXPIRED);
                }
                throw new Error(data.errorCode || 'Unauthorized');
            }
            return data.returnData;
        } catch (e) {
            return thunkAPI.rejectWithValue(isSessionError(e) ? SESSION_EXPIRED : 'Unauthorized');
        }
    }
);

export const userDataSlice = createSlice({
    name: 'userData',
    initialState,
    reducers: {
        clearState: () => initialState,
        // Reference counted rather than a plain on/off, so two screens wanting positions at the
        // same time cannot switch the feed off from under each other. See useOpenTradesFeed.
        retainOpenTradesFeed: (state) => {
            if (state.data) {
                state.data.openTradesConsumers = (state.data.openTradesConsumers ?? 0) + 1;
                state.data.openTradesActive = true;
            }
        },
        releaseOpenTradesFeed: (state) => {
            if (state.data) {
                state.data.openTradesConsumers = Math.max(0, (state.data.openTradesConsumers ?? 0) - 1);
                state.data.openTradesActive = state.data.openTradesConsumers > 0;
            }
        },
        changeNotificationsActive: (state, action: PayloadAction<boolean>) => {
            if (state.data) {
                state.data.notificationsActive = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        thunkGenericApiReducerBuilder(builder, loadUserData, (state: UserDataState, { loadData }: InitialData) => {
            state.data = {
                traderId: loadData.traderId,
                account: loadData.account,
                accountDetails: loadData.accountDetails,
                pendingMessagesCount: loadData.pendingMessagesCount,
                notificationsActive: false,
                openTradesActive: false,
                openTradesConsumers: 0,
            };
        });
        thunkGenericApiReducerBuilder(builder, loadUserKeepALiveData, (state: UserDataState, payload: KeepALive) => {
            if (state.data) {
                state.data.account.balanceValues = payload.balanceValues;
                state.data.pendingMessagesCount = payload.unreadMessages;
                if (state.data.openTradesActive && payload.returnTrades) {
                    state.data.account.positions = payload.trades;
                }
                if (state.data.notificationsActive && payload.returnPendingMessages) {
                    state.data.account.pendingMessages = payload.pendingMessages;
                }
            }
        });
    },
});

export const { clearState, retainOpenTradesFeed, releaseOpenTradesFeed, changeNotificationsActive } =
    userDataSlice.actions;

export default userDataSlice.reducer;
